// The death-register field group, shared across the places a deceased outcome is
// recorded. Which fields show depends on WHO is recording — see `mode`:
//
//   "observational" — Time of Death only. The nurse, at Initiate Discharge:
//                     she observes the death but does not certify it.
//   "clinical"      — Cause, Manner, Certifying Doctor, Certificate Number, Time
//                     of Death. The doctor's certification (a clinical act).
//   "full" (default)— everything. The Death Register's edit dialog, which is the
//                     catch-all correction surface.
//
// Controlled: the parent owns the values and receives a patch on every change.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, MenuItem, TextField, Typography } from "@mui/material";

import { useIpdData } from "../context/IpdDataContext";
import { MANNER_OF_DEATH_OPTIONS } from "../api/dischargeProcessApi";

export const EMPTY_DEATH_DETAILS = {
  causeOfDeath: "",
  mannerOfDeath: "",
  certifyingDoctorId: "",
  deathCertificateNumber: "",
  timeOfDeath: "",
};

export default function DeathDetailsFields({
  value,
  onChange,
  disabled = false,
  mode = "full",
}) {
  const { getAvailableDoctors } = useIpdData();

  // Certification is the doctor's; the nurse only observes the time of death.
  const showClinical = mode !== "observational";

  // Department -> doctor cascade, the same picker source as the doctor-console
  // handover UI and the transfer raise modal (get_all_medical_departments/ +
  // get_all_doctors_based_on_department/). The department is picker-only state
  // — only the chosen doctor_id is submitted.
  const [departments, setDepartments] = useState([]);
  const [department, setDepartment] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);

  useEffect(() => {
    if (!showClinical) return;
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}get_all_medical_departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.department_list) {
          setDepartments(res.data.department_list.map((d) => d.department));
        }
      })
      .catch(() => setDepartments([]));
  }, [showClinical]);

  const handleDepartmentChange = async (dept) => {
    setDepartment(dept);
    onChange({ certifyingDoctorId: "" });
    if (!dept) {
      setDoctors([]);
      return;
    }
    setDoctorsLoading(true);
    // assignedDoctorsOnly: false — the certifying doctor need not be the admitting
    // one, and the endpoint's default would hand back an empty list.
    const result = await getAvailableDoctors(dept, { assignedDoctorsOnly: false });
    setDoctors(result.data || []);
    setDoctorsLoading(false);
  };

  const set = (field) => (e) => onChange({ [field]: e.target.value });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Cause, manner, certifying doctor and time of death are the four fields
          the body cannot be released without (the backend gates discharge on
          them). Everything here stays editable after the fact — a corrected time
          of death and a certificate number routinely arrive days later. */}
      {showClinical && (
        <>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              Cause of Death *
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              disabled={disabled}
              placeholder="Immediate and underlying cause"
              value={value.causeOfDeath}
              onChange={set("causeOfDeath")}
            />
          </Box>

          <TextField
            select
            fullWidth
            size="small"
            label="Manner of Death *"
            disabled={disabled}
            value={value.mannerOfDeath}
            onChange={set("mannerOfDeath")}
          >
            <MenuItem value="">
              <em>Not recorded</em>
            </MenuItem>
            {MANNER_OF_DEATH_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Department"
              disabled={disabled}
              value={department}
              onChange={(e) => handleDepartmentChange(e.target.value)}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Certifying Doctor *"
              disabled={disabled || !department || doctorsLoading}
              value={value.certifyingDoctorId}
              onChange={set("certifyingDoctorId")}
            >
              <MenuItem value="">
                <em>{doctorsLoading ? "Loading…" : "Select"}</em>
              </MenuItem>
              {doctors.map((doc) => (
                <MenuItem key={doc.doctor_id} value={String(doc.doctor_id)}>
                  {doc.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Death Certificate Number"
            disabled={disabled}
            value={value.deathCertificateNumber}
            onChange={set("deathCertificateNumber")}
          />
        </>
      )}

      <TextField
        fullWidth
        size="small"
        type="datetime-local"
        label="Time of Death *"
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        value={value.timeOfDeath}
        onChange={set("timeOfDeath")}
        helperText="Must be earlier than the physical discharge time. Editable later — correct it to the actual time of death."
      />
    </Box>
  );
}
